import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { toast } from "sonner";
import { useAuth } from "./useAuth";
import { trackFeature } from "@/lib/analytics";
import { buildGoogleAuthUrl } from "@/lib/googleConnectUrl";

const GOOGLE_CAL_URL = "https://unnzlorzyswiahjlxwqh.supabase.co/functions/v1/google-calendar-auth";
const ANON_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InVubnpsb3J6eXN3aWFoamx4d3FoIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzYxMDU5NTMsImV4cCI6MjA5MTY4MTk1M30.4zmLgxfgFlg9mnvkqQVPgsmV8bP1UO_OyLb3wXjgSfo";

const GOOGLE_CLIENT_ID = "880985955970-kpr3856tpa6oeeil9sm36l5s1a53ef57.apps.googleusercontent.com";
const REDIRECT_URI = `${window.location.origin}/google/callback`;
const SCOPES = "https://www.googleapis.com/auth/calendar.events";

const isMedian = () =>
  typeof navigator !== "undefined" && /median/i.test(navigator.userAgent);
// #1798 — iPhone Safari (the web app, not the shell) hit the same dead end the
// shell did: the callback's iPhone guard never attempted the exchange. It gets
// the ticket too, and a same-tab navigation instead of window.open, which
// Safari would block after the await in connect().
const isMobileApple = () =>
  typeof navigator !== "undefined" && /iphone|ipod|ipad/i.test(navigator.userAgent);

interface GoogleCalendarContextValue {
  connected: boolean;
  checking: boolean;
  /** #1798 — async because Median and iPhone-Safari callers mint a one-time
   *  ticket first. Desktop callers never await, so their window.open stays
   *  inside the click's user-activation window and is not popup-blocked. */
  connect: () => Promise<void>;
  /** Resolves true only when the server confirmed the token row is deleted.
   *  false on no session, a non-2xx, or a network failure — and `connected`
   *  is left untouched then, because nothing was proved (#1806). Callers that
   *  tell her "disconnected" must read this first. */
  disconnect: () => Promise<boolean>;
  addEvents: (events: AddEventInput[]) => Promise<unknown>;
  /** #1980 — patch events already in Google. */
  updateEvents: (events: Array<Partial<AddEventInput> & { google_event_id: string }>) => Promise<{ updated: number; total: number }>;
  fetchEvents: (action: "get_today" | "get_week" | "get_month") => Promise<CalEvent[]>;
  /** #1659 — an arbitrary window for the month grid and the 28-day strip.
   *  Resolves to [] on any failure rather than throwing: a dead Google token
   *  must cost her those events and nothing else, and the DB rows still render.
   *  Mirrors useAppleCalendar's fetchRange exactly. */
  fetchRange: (startIso: string, endIso: string) => Promise<CalEvent[]>;
  checkStatus: () => Promise<void>;
}

interface AddEventInput {
  title: string;
  start_datetime: string;
  end_datetime: string;
  location: string;
  timezone: string;
  notes: string;
  /** Read by buildGoogleEvent server-side for a date-valued all-day event. */
  all_day?: boolean;
}

interface CalEvent {
  title: string;
  start: string;
  end?: string;
  location?: string;
  // #1659 — the month/strip merge needs these. The function has returned them
  // on every read action since #606 (googleEventIdentity); the type here just
  // never named them, because the week merge reached the raw JSON directly.
  // `uid` / `recurrence_id` are what let calendarEventKey take its real branch
  // instead of the synthetic `start|title` fallback, which is how two same-time
  // same-title events used to collapse into one.
  all_day?: boolean;
  uid?: string | null;
  recurrence_id?: string | null;
}

const GoogleCalendarContext = createContext<GoogleCalendarContextValue | null>(null);

export function GoogleCalendarProvider({ children }: { children: ReactNode }) {
  const { user, session } = useAuth();

  /**
   * Auth headers for every google-calendar-auth call (#494).
   *
   * Was `Bearer ${ANON_KEY}` — the publishable key, which ships in this bundle
   * and is public. The Edge Function then trusted the user_id in the body while
   * querying with a service-role client, so anyone at all could read, write to,
   * or disconnect any user's calendar given their UUID. The function now derives
   * the acting user from this token, so it has to be a real session.
   *
   * Token read from useAuth(), never supabase.auth.getSession() — see the
   * June 29 lock-starvation note in CLAUDE.md.
   *
   * apikey stays the anon key: that header is Supabase's project router, not an
   * identity claim.
   */
  const authHeaders = useCallback((): Record<string, string> | null => {
    const token = session?.access_token;
    if (!token) return null;
    return {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      apikey: ANON_KEY,
    };
  }, [session?.access_token]);
  const [connected, setConnected] = useState(false);
  const [checking, setChecking] = useState(true);

  const checkStatus = useCallback(async () => {
    if (!user) {
      setConnected(false);
      setChecking(false);
      return;
    }
    const h = authHeaders();
    if (!h) { setConnected(false); setChecking(false); return; }
    try {
      const resp = await fetch(GOOGLE_CAL_URL, {
        method: "POST",
        headers: h,
        body: JSON.stringify({ action: "check_status", user_id: user.id }),
      });
      if (resp.ok) {
        const data = await resp.json();
        setConnected(!!data.connected);
      }
    } catch {
      // ignore
    } finally {
      setChecking(false);
    }
  }, [user, authHeaders]);

  useEffect(() => { checkStatus(); }, [checkStatus]);

  // #1798 — re-check when she comes BACK. The connect flow now finishes in a
  // Safari tab she then leaves; nothing in this provider used to notice. The
  // Median shell delivers no DOM lifecycle events on resume (no visibilitychange,
  // no focus) — it invokes a GLOBAL `median_app_resumed()` if the page defined
  // one. Single slot, not a listener list: chain whatever was there and restore
  // it on cleanup. Same shape as EntitlementFlipListener; CLAUDE.md records this
  // as the fourth place that wall has been hit. The two DOM events cover web.
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === "visible") void checkStatus();
    };
    const onFocus = () => { void checkStatus(); };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", onFocus);
    const globalWindow = window as unknown as Record<string, unknown>;
    const previousResumeHandler = globalWindow.median_app_resumed;
    globalWindow.median_app_resumed = (...args: unknown[]) => {
      void checkStatus();
      if (typeof previousResumeHandler === "function") {
        try {
          (previousResumeHandler as (...a: unknown[]) => unknown)(...args);
        } catch {
          /* someone else's handler, not ours to fix */
        }
      }
    };
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", onFocus);
      globalWindow.median_app_resumed = previousResumeHandler;
    };
  }, [checkStatus]);

  /**
   * #1798 — ask the function for a one-time ticket to carry as OAuth `state`.
   * Null on ANY failure, and on the two surfaces that need a ticket null means
   * STOP (see connect): launching Google without one would send her through a
   * consent screen whose callback can only end on the expired-link page.
   * Copilot on #1804 — an earlier version "fell back" to a ticketless URL, which
   * was a known dead end dressed up as graceful degradation.
   */
  const mintConnectTicket = useCallback(async (): Promise<string | null> => {
    if (!user) return null;
    const h = authHeaders();
    if (!h) return null;
    try {
      const resp = await fetch(GOOGLE_CAL_URL, {
        method: "POST",
        headers: h,
        body: JSON.stringify({ action: "mint_connect_ticket", user_id: user.id }),
      });
      if (!resp.ok) {
        console.warn(`[useGoogleCalendar] mint_connect_ticket ${resp.status} — opening Google without a ticket`);
        return null;
      }
      const data = await resp.json();
      return typeof data?.ticket === "string" && data.ticket ? data.ticket : null;
    } catch (e) {
      console.warn("[useGoogleCalendar] mint_connect_ticket threw — opening Google without a ticket", e);
      return null;
    }
  }, [user, authHeaders]);

  // #1798 — one connect at a time. Each mint SUPERSEDES the user's previous
  // pending ticket, so two taps during the await would invalidate the first
  // ticket while both invocations still opened Google — and completing the
  // first consent screen would then always fail redemption (Copilot on #1804).
  // A ref, not state: it must be read synchronously inside the click.
  const connectInFlightRef = useRef(false);

  const connect = useCallback(async () => {
    trackFeature("calendar_sync", { action: "connect_initiated" });
    const urlFor = (ticket: string | null) =>
      buildGoogleAuthUrl({ clientId: GOOGLE_CLIENT_ID, redirectUri: REDIRECT_URI, scopes: SCOPES, ticket });

    // Desktop / Android browser: UNCHANGED and synchronous. The callback lands
    // in a tab of the same browser, which holds the session, so no ticket is
    // needed — and no await may precede window.open or Safari-on-Mac blocks it
    // as a popup. The ticket is for the two places the session is NOT there.
    if (!isMedian() && !isMobileApple()) {
      window.open(urlFor(null), "_blank");
      return;
    }

    if (connectInFlightRef.current) return;
    connectInFlightRef.current = true;
    try {
      const ticket = await mintConnectTicket();
      if (!ticket) {
        // Stop here. Without a ticket the callback can only land on the
        // expired-link screen, after she has sat through Google's consent —
        // a retryable error now is honest; a doomed launch is not.
        toast.error("Couldn't start the Google Calendar connection — give it another tap in a sec.");
        return;
      }
      const authUrl = urlFor(ticket);

      if (isMedian()) {
        try {
          const median = (window as any).median;
          if (median?.window?.open) {
            median.window.open(authUrl, "external");
            return;
          }
        } catch { /* fall through */ }
        try {
          window.location.href = `median://window/open?url=${encodeURIComponent(authUrl)}&mode=external`;
          return;
        } catch { /* fall through */ }
        window.location.href = authUrl;
        return;
      }

      // iPhone Safari, the web app: same tab, not window.open — a popup after an
      // await is blocked, and the ticket means the callback can finish here.
      window.location.href = authUrl;
    } finally {
      // Released as soon as Google has been opened (or we gave up), so a genuine
      // retry after she comes back from Safari is never blocked.
      connectInFlightRef.current = false;
    }
  }, [mintConnectTicket]);

  const disconnect = useCallback(async (): Promise<boolean> => {
    if (!user) return false;
    const h = authHeaders();
    if (!h) return false; // no session: fail closed locally rather than collect a 403
    try {
      const resp = await fetch(GOOGLE_CAL_URL, {
        method: "POST",
        headers: h,
        body: JSON.stringify({ action: "disconnect", user_id: user.id }),
      });
      // #1806 — only a 2xx proves the token row is gone (the server now 500s
      // when its delete fails). Flipping `connected` on anything else would
      // last exactly until the next checkStatus(), which reads the row.
      if (!resp.ok) return false;
      setConnected(false);
      return true;
    } catch {
      return false;
    }
  }, [user, authHeaders]);

  const addEvents = useCallback(async (events: AddEventInput[]) => {
    if (!user) throw new Error("Not authenticated");
    const h = authHeaders();
    if (!h) throw new Error("Not authenticated");
    const resp = await fetch(GOOGLE_CAL_URL, {
      method: "POST",
      headers: h,
      body: JSON.stringify({ action: "add_events", user_id: user.id, events }),
    });
    if (!resp.ok) throw new Error("Failed to add events");
    return resp.json();
  }, [user, authHeaders]);

  /**
   * #1980 — patch events she edited in Kin that already have a Google copy.
   * Resolves with per-event results; never throws on a partial failure, so the
   * caller can say exactly what did and didn't reach Google.
   */
  const updateEvents = useCallback(async (
    events: Array<Partial<AddEventInput> & { google_event_id: string }>,
  ): Promise<{ updated: number; total: number }> => {
    if (!user) throw new Error("Not authenticated");
    const h = authHeaders();
    if (!h) throw new Error("Not authenticated");
    const resp = await fetch(GOOGLE_CAL_URL, {
      method: "POST",
      headers: h,
      body: JSON.stringify({ action: "update_events", user_id: user.id, events }),
    });
    if (resp.status === 401 || resp.status === 403) {
      setConnected(false);
      return { updated: 0, total: events.length };
    }
    if (!resp.ok) return { updated: 0, total: events.length };
    const data = await resp.json().catch(() => null);
    const updated = Number(data?.total_updated ?? 0);
    return { updated: Number.isFinite(updated) ? updated : 0, total: events.length };
  }, [user, authHeaders]);

  const fetchEvents = useCallback(async (action: "get_today" | "get_week" | "get_month") => {
    if (!user) throw new Error("Not authenticated");
    const h = authHeaders();
    if (!h) throw new Error("Not authenticated");
    const resp = await fetch(GOOGLE_CAL_URL, {
      method: "POST",
      headers: h,
      body: JSON.stringify({ action, user_id: user.id }),
    });
    if (resp.status === 401 || resp.status === 403) {
      // Token revoked or expired — flip global state so UI updates.
      setConnected(false);
      throw new Error("TOKEN_EXPIRED");
    }
    if (!resp.ok) throw new Error("Failed to fetch events");
    const data = await resp.json();
    return (data.events || []) as CalEvent[];
  }, [user, authHeaders]);

  const fetchRange = useCallback(async (startIso: string, endIso: string): Promise<CalEvent[]> => {
    if (!user) return [];
    const h = authHeaders();
    if (!h) return [];
    try {
      const resp = await fetch(GOOGLE_CAL_URL, {
        method: "POST",
        headers: h,
        body: JSON.stringify({ action: "get_range", user_id: user.id, window_start: startIso, window_end: endIso }),
      });
      if (resp.status === 401 || resp.status === 403) {
        // Token revoked — flip global state so the UI offers reconnect, the
        // same as fetchEvents. Unlike fetchEvents this does NOT throw: the
        // month grid calls it as a garnish on rows it has already loaded, and
        // an exception there would take the whole grid down with it.
        setConnected(false);
        return [];
      }
      if (!resp.ok) return [];
      const data = await resp.json();
      return Array.isArray(data.events) ? (data.events as CalEvent[]) : [];
    } catch {
      return [];
    }
  }, [user, authHeaders]);

  const value = useMemo<GoogleCalendarContextValue>(
    () => ({ connected, checking, connect, disconnect, addEvents, updateEvents, fetchEvents, fetchRange, checkStatus }),
    [connected, checking, connect, disconnect, addEvents, updateEvents, fetchEvents, fetchRange, checkStatus],
  );

  return (
    <GoogleCalendarContext.Provider value={value}>
      {children}
    </GoogleCalendarContext.Provider>
  );
}

export function useGoogleCalendar(): GoogleCalendarContextValue {
  const ctx = useContext(GoogleCalendarContext);
  if (!ctx) throw new Error("useGoogleCalendar must be used within <GoogleCalendarProvider>");
  return ctx;
}
